import type { ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Tooltip from 'LuxKit/Tooltip'
import Badge from 'LuxKit/Badge'
import Group from 'components/utils/Group'

import { toBaseFareClass } from 'lib/flights/rewardFareClasses'
import type { FareClassWithReward } from 'constants/flight'
import { CHARTER_CABIN_CONTENT, LineCarIcon } from './charterCabinContent'

interface Props {
  fareClass: FareClassWithReward
  section: 'inclusions' | 'baggage'
}

/**
 * Renders the per-cabin amenity list (inclusions or baggage) sourced from
 * {@link CHARTER_CABIN_CONTENT}. Used by the fare-class modal cards and the
 * trip-options-table expanded row so the two surfaces share copy + icons.
 */
function CharterCabinFeatureList({ fareClass, section }: Props) {
  const intl = useIntl()
  const content = CHARTER_CABIN_CONTENT[toBaseFareClass(fareClass)]
  if (!content) return null

  const items = section === 'inclusions' ? content.inclusions : content.baggage

  return <>
    {items.map((amenity, i) => {
      if (amenity.icon === LineCarIcon) {
        return (
          <CaptionTextBlock
            key={i}
            variant="medium"
            startIcon={<amenity.icon />}
            endIcon={
              <Tooltip
                description={intl.formatMessage({
                  id: '9YhUIqIqMuGyui3siwDv',
                  defaultMessage: 'SYD/BNE must book domestic flights with their charter package to be eligible',
                  description: 'Tooltip explaining eligibility for home to airport transfers in charter flights',
                })}
                placement="top"
              >
                <LineInfoCircleIcon colour="neutral-two" size={14} />
              </Tooltip>
            }
          >
            <FormattedMessage
              id="Y-Dw8m2rFgKcyn5hy7J5"
              defaultMessage="<badge>New</badge> Home to airport transfers (max 40km) in MEL/SYD/BNE"
              description="Charter flight business amenity with New badge: home to airport transfer service"
              values={{
                badge: (chunks: ReactNode) => (
                  <Group direction="horizontal" verticalAlign="center" gap={0}>
                    <Badge mode="critical" kind="string">{chunks}</Badge>
                  </Group>
                ),
              }}
            />
          </CaptionTextBlock>
        )
      }

      return (
        <CaptionTextBlock key={i} variant="medium" startIcon={<amenity.icon />}>
          {intl.formatMessage(amenity.message)}
        </CaptionTextBlock>
      )
    })}
  </>
}

export default CharterCabinFeatureList
